import React, { useState, useEffect, useRef } from 'react';
import '../styles.css';
import { motion } from 'framer-motion';
import {
  Wrench,
  ShieldCheck,
  Bug,
  Settings,
  UserCheck,
  HardDriveDownload,
  MonitorSmartphone,
  Cloud,
  ChevronLeft
} from 'lucide-react';
import {
  FaInstagram,
  FaWhatsapp,
  FaGoogle,
  FaFacebookF,
  FaTiktok,
  FaYoutube
} from 'react-icons/fa';

import logoApple from '../assets/apple.png';
import logoXiaomi from '../assets/xiaomi.png';
import logoLG from '../assets/lg.png';
import logoHP from '../assets/hp.png';
import logoDell from '../assets/dell.png';
import logoAsus from '../assets/asus.png';
import logoLenovo from '../assets/lenovo.png';
import logoWindows from '../assets/windows.png';
import movilRoto from '../assets/movilroto.png';
import taller1 from '../assets/taller1.jpg';
import taller2 from '../assets/taller2.jpg';
import taller3 from '../assets/taller3.jpg';

const logos = [
  { src: logoApple, alt: 'Apple', className: 'logo-pequeno' },
  { src: logoXiaomi, alt: 'Xiaomi' },
  { src: logoLG, alt: 'LG' },
  { src: logoHP, alt: 'HP', className: 'logo-hp' },
  { src: logoDell, alt: 'Dell' },
  { src: logoAsus, alt: 'Asus' },
  { src: logoLenovo, alt: 'Lenovo' },
  { src: logoWindows, alt: 'Windows' },
];

const testimonios = [
  {
    nombre: "Javier",
    estrellas: 5,
    comentario:
      "El técnico tuvo muy buen trato, solucionó todos los problemas con eficacia y rápidez, calidad y precio sobresaliente, lo recomiendo sin duda.",
  },
  {
    nombre: "Sonia Alvarez",
    estrellas: 5,
    comentario:
      "Fui para recuperar archivos muy importantes de un disco duro que ya daba por perdidos, y me los recuperó todos en nada de tiempo. Lo recomiendo al 100 %.",
  },
  {
    nombre: "Isaac Gómez",
    estrellas: 5,
    comentario:
      "Me hicieron mantenimiento a mi PS4 y ahora no hace absolutamente nada de ruido. El trabajo fue de 10, impecable.",
  },
];

const faqData = [
  {
    question: "¿Qué tipo de dispositivos reparáis?",
    answer: "Reparamos ordenadores de sobremesa, portátiles, móviles, tablets, consolas y algunos periféricos.",
  },
  {
    question: "¿Trabajáis con todas las marcas?",
    answer: "Sí, trabajamos con marcas como Apple, HP, Dell, Lenovo, Asus, Acer, Samsung, Xiaomi, entre otras...",
  },
  {
    question: "¿Cuánto tardáis en hacer una reparación?",
    answer: "Depende del problema, pero la mayoría de reparaciones se completan entre 24 y 72 horas. Si hay que pedir piezas, te avisamos con antelación.",
  },
  {
    question: "¿Perderé mis datos durante la reparación?",
    answer: "Siempre que sea posible, intentamos conservar todos tus datos. De todas formas, te recomendamos tener una copia de seguridad. Si no sabes cómo hacerla, ¡te ayudamos!",
  },
  {
    question: "¿Ofrecéis garantía tras la reparación?",
    answer: "Por supuesto. Todas nuestras reparaciones tienen garantía de 3 a 6 meses, según el tipo de servicio o componente reemplazado.",
  },
];

const Inicio = () => {
  const [index, setIndex] = useState(0);
  const visibleCount = 4;
  const sliderRef = useRef();

  const [currentIndex, setCurrentIndex] = useState(0); // Para testimonios

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonios.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const next = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
    setIndex((prev) => (prev + 1) % logos.length);
  };

  const prev = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
    setIndex((prev) => (prev - 1 + logos.length) % logos.length);
  };

  return (
    <div className="page-wrapper">
      <main className="contenido-box fadeIn">
        <header className="inicio-header">
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <h1>¿PROBLEMAS CON TU DISPOSITIVO?</h1>
          <p>
            En Duxbyte Informática sabemos lo que significa depender de tus dispositivos.
            Por eso, te ofrecemos soluciones rápidas y precisas para cualquier problema: desde ordenadores y portátiles, hasta móviles, tablets y periféricos.
            Nuestro equipo experto se encarga tanto de averías de hardware como de fallos de software o infecciones de virus, con un trato cercano y sin tecnicismos que te compliquen.
            Tu tranquilidad es nuestra prioridad, porque queremos que tu tecnología funcione sin problemas para que tú solo te preocupes de lo importante.
          </p>
          <br />
          <img src={movilRoto} alt="Soporte Técnico" className="inicio-header-img" /><br />
        </header>

        <motion.section className="seccion contacto-cta delay-7" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.2 }}>
          <h2 className="titulo-izquierda">¿Tienes un problema? ¡Pregunta sin compromiso!</h2>
          <button onClick={() => window.location.href = '/contacto'}>Contactar</button>
        </motion.section>

        <motion.section className="seccion services delay-1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h2 className="titulo-izquierda">Servicios destacados</h2>
          <div className="servicios-grid">
            <div className="servicio-card"><Wrench size={36} /><p>Diagnóstico gratuito y sin compromiso</p></div>
            <div className="servicio-card"><ShieldCheck size={36} /><p>Reparaciones de hardware y software</p></div>
            <div className="servicio-card"><Bug size={36} /><p>Eliminación de virus y malware</p></div>
            <div className="servicio-card"><Settings size={36} /><p>Instalación y configuración</p></div>
            <div className="servicio-card"><UserCheck size={36} /><p>Asesoría personalizada</p></div>
            <div className="servicio-card"><HardDriveDownload size={36} /><p>Recuperación de datos</p></div>
            <div className="servicio-card"><MonitorSmartphone size={36} /><p>Configuración a medida de PC</p></div>
            <div className="servicio-card"><Cloud size={36} /><p>Soluciones en la nube</p></div>
          </div>
        </motion.section>

        <motion.section className="seccion testimonios delay-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }}>
          <h2 className="titulo-izquierda">Lo que opinan nuestros clientes</h2>
          <div className="testimonio-carrusel">
            <motion.div
              key={currentIndex}
              className="testimonio-sin-foto"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
            >
              <p>“{testimonios[currentIndex].comentario}”</p>
              <div className="testimonio-autor">
                — {testimonios[currentIndex].nombre}<br />
                <span className="estrellas">
                  {"★".repeat(testimonios[currentIndex].estrellas)}
                  {"☆".repeat(5 - testimonios[currentIndex].estrellas)}
                </span>
              </div>
            </motion.div>
          </div>
        </motion.section>

        <motion.section className="seccion delay-4 empresas-trabajo" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}>
          <h2 className="titulo-izquierda">Marcas de dispositivos que reparamos</h2>
          <div className="slider-container">
            <button className="arrow left" onClick={prev}><ChevronLeft size={24} /></button>
            <div className="partners-scroll" ref={sliderRef}>
              {logos.map(({ src, alt, className }) => (
                <img key={alt} src={src} alt={alt} className={className || ''} />
              ))}
            </div>
            <button className="arrow right" onClick={next}>&#9658;</button>
          </div>
        </motion.section>

        <motion.section className="seccion galeria delay-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1 }}>
          <h2 className="titulo-centrado">Galería</h2>
          <div className="galeria">
            <img src={taller1} alt="Taller 1" />
            <img src={taller2} alt="Taller 2" />
            <img src={taller3} alt="Taller 3" />
          </div>
        </motion.section>

        {/* 🔗 NUEVA SECCIÓN: Redes Sociales */}
        <motion.section
          className="seccion redes delay-7"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
        >
          <h2 className="titulo-izquierda">Nuestras Redes Sociales</h2>
          <div className="redes-enlaces">
            <a
              href="https://www.instagram.com/duxbyte"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon instagram"
            >
              <FaInstagram />
            </a>
            <a
              href="https://wa.me/34654588511"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon whatsapp"
            >
              <FaWhatsapp />
            </a>
            <a
              href="https://www.google.com/maps/place/Duxbyte"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon google"
            >
              <FaGoogle />
            </a>
            <a
              href="https://www.facebook.com/duxbyte"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon facebook"
            >
              <FaFacebookF />
            </a>
            <a
              href="https://www.tiktok.com/@duxbyte"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon tiktok"
            >
              <FaTiktok />
            </a>
            <a
              href="https://www.youtube.com/@duxbyte"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon youtube"
            >
              <FaYoutube />
            </a>
          </div>
        </motion.section>


        <motion.section className="seccion delay-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.4 }}>
          <h2 className="titulo-izquierda">Preguntas Frecuentes (FAQ)</h2>
          <div className="faq-container">
            {faqData.map((item, index) => (
              <FaqItem key={index} question={item.question} answer={item.answer} />
            ))}
          </div>
        </motion.section>
      </main>
    </div>
  );
};

const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      layout
      onClick={() => setIsOpen(!isOpen)}
      className={`faq-item ${isOpen ? 'open' : ''}`}
      initial={{ borderRadius: 10 }}
    >
      <motion.div layout="position" className="faq-question">
        {question}
        <span className="faq-toggle">{isOpen ? '−' : '+'}</span>
      </motion.div>
      {isOpen && (
        <motion.div
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="faq-answer"
        >
          {answer}
        </motion.div>
      )}
    </motion.div>
  );
};

export default Inicio;
